import { useEffect } from 'react'
import { useRouter } from 'next/router'

import { selectUserId } from '~/auth/slices/auth.slice'
import dashboardAnalytics from '~/analytics/dashboard/dashboardAnalytics'
import curriculumSanity from '~/shared/sanity/curriculum'
import { DASHBOARD_SELECT_GRADE_ORIGIN, SELECT_GRADE_ORIGIN_PARAM } from '~/curriculum/constants/curriculum.const'
import { getCurriculumRelevantCountryCodeAndGradeId } from '~/shared/sanity/utils/curriculum.utils'
import { useGetCurrentGradeQuery, useGetCurriculumDataQuery } from '~/curriculum/apis/curriculum.api'
import { useGetUserChosenCurriculumCountryCode } from '~/curriculum/hooks/useGetCurriculumCountryCode'
import { selectGradeListByCountry, setGradeListByCountryActionCreator } from '~/curriculum/slice/curriculum.slice'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { MustKnowsProgressCardProps } from '~/statistics-v2/types/statistics.types'
import useGetCorrectedCountryCode from '~/curriculum/hooks/useGetCorrectedCountryCode'

import useMasteryTrackV2SubtopicStats from './useMasteryTrackV2SubtopicStats'
import useGetCurriculumRedirectionUrl from './useGetCurriculumRedirection'
import useGetGradeOverviewPageRoute from './useGetGradeOverviewPageRoute'
import useGetGradePageRoute from './useGetGradePageRoute'

interface UseMasteryTrackPanelModelResult {
  data: ReturnType<typeof useGetCurriculumDataQuery>['data']
  gradeData: ReturnType<typeof useGetCurrentGradeQuery>['data']
  isLoading: boolean
  isGradeLoading: boolean
  title: string | null | undefined
  browseCurriculumUrl: ReturnType<typeof useGetGradeOverviewPageRoute>
  startLinkUrl: ReturnType<typeof useGetCurriculumRedirectionUrl>
  onChangeCurriculumGrade: () => void
  onAddCurriculum: () => void
  mustKnowsProgressCardProps: MustKnowsProgressCardProps
  topicProgressPercentage: number | undefined
  hasSubtopicStats: boolean
}

const useMasteryTrackPanelModel = (): UseMasteryTrackPanelModelResult => {
  const dispatch = useAppDispatch()
  const router = useRouter()

  const userId = useAppSelector(selectUserId)
  const gradeList = useAppSelector(selectGradeListByCountry)
  const countryCode = useGetUserChosenCurriculumCountryCode()
  const correctedCountryCode = useGetCorrectedCountryCode(countryCode)
  const selectGradeUrl = useGetGradePageRoute()

  const { data: gradeData, isLoading: isGradeLoading } = useGetCurrentGradeQuery()
  const { data, isLoading } = useGetCurriculumDataQuery(
    {
      countryCode: correctedCountryCode
        ? getCurriculumRelevantCountryCodeAndGradeId(correctedCountryCode).curriculumCountryCode
        : correctedCountryCode,
      grade: gradeData?.grade ?? 0,
    },
    { skip: !userId || !correctedCountryCode || isGradeLoading },
  )

  // Temporary replacement for `useGetUserStats` while `/v1/progress/get-user-stats` is too slow.
  // Revert by swapping this call back to the `useGetUserStats(...)` block (see git history of this file).
  const { topicProgressPercentage, mustKnowsProgressCardProps, hasSubtopicStats } = useMasteryTrackV2SubtopicStats({
    correctedCountryCode,
    gradeData,
  })

  const curriculumGradeOverviewUrl = useGetGradeOverviewPageRoute({
    grade: data?.grade ?? 0,
    topicOrder: data?.subjectOrder ?? 0,
  })

  const onAddCurriculum = (): void => {
    dashboardAnalytics.logButtonClick('add_your_curriculum')
    router.push(`${selectGradeUrl}?${SELECT_GRADE_ORIGIN_PARAM}=${DASHBOARD_SELECT_GRADE_ORIGIN}`)
  }

  const onChangeCurriculumGrade = (): void => {
    dashboardAnalytics.logButtonClick('change_curriculum')
    router.push(`${selectGradeUrl}?${SELECT_GRADE_ORIGIN_PARAM}=${DASHBOARD_SELECT_GRADE_ORIGIN}`)
  }

  const title = gradeData?.grade ? gradeList.find((el) => el.gradeLevel === gradeData.grade)?.title : null
  const startLinkUrl = useGetCurriculumRedirectionUrl({
    specificGrade: gradeData?.grade ?? undefined,
    specificCountryCode: countryCode,
    specificSubjectOrder: gradeData?.subjectOrder ?? undefined,
    specificSubTopic: gradeData?.subTopic ?? undefined,
    specificSubTopicOrder: gradeData?.subTopicOrder ?? undefined,
  })
  useEffect(() => {
    if (countryCode) {
      curriculumSanity.getCurriculumGradeListByCountry(countryCode, 'v2').then((response) => {
        dispatch(setGradeListByCountryActionCreator(response))
      })
    }
  }, [countryCode, dispatch])

  const browseCurriculumUrl = curriculumGradeOverviewUrl

  return {
    data,
    gradeData,
    isLoading,
    isGradeLoading,
    title,
    browseCurriculumUrl,
    startLinkUrl,
    onChangeCurriculumGrade,
    onAddCurriculum,
    mustKnowsProgressCardProps,
    topicProgressPercentage,
    hasSubtopicStats,
  }
}

export default useMasteryTrackPanelModel
